import { supabase } from './supabase';

export const PAGE_VIEW_DEDUP_MS = 30_000;
const STORAGE_KEY = 'dp_last_page_view';

const EXCLUDED_PREFIXES = ['/auth', '/login', '/logout', '/embed-dev', '/dev', '/oidc', '/callback'];

/** Chemin sans query ni hash, sans slash final. Jamais de données d'URL sensibles. */
export function normalizePagePath(pathname: string): string {
  const base = (pathname || '/').split('?')[0].split('#')[0];
  return base.length > 1 ? base.replace(/\/+$/, '') || '/' : base || '/';
}

export function isTrackablePath(pathname: string): boolean {
  const p = normalizePagePath(pathname);
  return !EXCLUDED_PREFIXES.some((x) => p === x || p.startsWith(`${x}/`));
}

let lastMemory: { key: string; ts: number } | null = null;

/** Déduplication client : même (user, ligue, page) sous 30 s (refresh, StrictMode, remontage). */
export function shouldRecordPageView(key: string, now: number = Date.now()): boolean {
  let last = lastMemory;
  if (!last) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) last = JSON.parse(raw);
    } catch {
      /* ignore */
    }
  }
  if (last && last.key === key && now - last.ts < PAGE_VIEW_DEDUP_MS) return false;
  lastMemory = { key, ts: now };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lastMemory));
  } catch {
    /* ignore */
  }
  return true;
}

export function resetPageViewDedup(): void {
  lastMemory = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Une requête RPC par visite retenue. Échec silencieux. */
export async function recordPageView(userId: number, leagueId: number, pathname: string): Promise<void> {
  try {
    await supabase.rpc('record_page_view', {
      p_user_id: userId,
      p_league_id: leagueId,
      p_page_path: normalizePagePath(pathname),
    });
  } catch {
    /* silencieux */
  }
}

export interface PageViewStats {
  league_id: number;
  days: number | null;
  tracking_start: string | null;
  previous_complete: boolean;
  unique_users: number;
  total_views: number;
  previous_unique_users: number;
  previous_total_views: number;
  daily: { day: string; views: number; unique_users: number }[];
  top_pages: { page_path: string; views: number; unique_users: number }[];
}

export async function fetchPageViewStats(
  userId: number,
  leagueId: number,
  days: number | null
): Promise<{ data: PageViewStats | null; error: unknown }> {
  const { data, error } = await supabase.rpc('ci_get_page_view_stats', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_days: days,
  });
  if (error) return { data: null, error };
  return { data: (data as PageViewStats) ?? null, error: null };
}
